<template>
  <div class="company-onboarding-page">
    <v-container>
      <v-layout justify-center>
        <v-flex xs12 md10 lg8>
          <v-card class="onboarding-card elevation-3">
            <div class="onboarding-header primary white--text">
              <v-icon dark class="header-icon">business</v-icon>
              <div>
                <div class="headline">Registro de empresa</div>
                <div v-if="tokenValid" class="subheading header-subtitle">{{ companyName }} · {{ buildingName }}</div>
              </div>
            </div>

            <div v-if="validating" class="text-xs-center pa-5">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-3 grey--text text--darken-1">Validando invitación...</p>
            </div>

            <v-card-text v-else-if="!tokenValid">
              <v-alert type="error" :value="true">
                {{ generalError || 'El enlace es inválido o ha expirado.' }}
              </v-alert>
            </v-card-text>

            <div v-else-if="completed" class="text-xs-center pa-5">
              <v-avatar size="96" color="green lighten-5" class="mb-3">
                <v-icon color="success" size="64">check_circle</v-icon>
              </v-avatar>
              <h2 class="headline mt-2">Registro completado</h2>
              <p class="grey--text text--darken-1 mt-2">{{ companyName }} ya está activa en Intrare.</p>
              <v-btn color="primary" large to="/">Ir a iniciar sesión</v-btn>
            </div>

            <v-stepper v-else v-model="currentStep" class="onboarding-stepper elevation-0">
              <v-stepper-header class="elevation-0">
                <v-stepper-step :complete="currentStep > 1" step="1" editable>Administrador</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="currentStep > 2" step="2" :editable="currentStep > 1">Empresa</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step step="3" :editable="currentStep > 2">Documentos</v-stepper-step>
              </v-stepper-header>

              <v-stepper-items>
                <!-- Paso 1: administrador -->
                <v-stepper-content step="1">
                  <v-alert type="info" :value="true" outline class="mb-4">
                    La cuenta del administrador se creará con <strong>{{ adminEmail }}</strong>.
                    El enlace vence el {{ expiresAt }}.
                  </v-alert>
                  <v-form ref="adminForm">
                    <div class="step-title">Datos del administrador</div>
                    <div class="step-hint">Esta persona gestionará la cuenta de la empresa.</div>
                    <v-layout row wrap>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field v-model="form.first_name" label="Nombre" prepend-icon="person" :rules="requiredRules" :error-messages="fieldErrors('first_name')"></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field v-model="form.last_name" label="Apellidos" :rules="requiredRules" :error-messages="fieldErrors('last_name')"></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field v-model="form.celular" label="Teléfono celular" prepend-icon="phone_android" :rules="phoneRules" :error-messages="fieldErrors('celular')"></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field v-model="form.position" label="Puesto" prepend-icon="work"></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field
                          v-model="form.password"
                          label="Contraseña"
                          prepend-icon="lock"
                          :type="showPassword ? 'text' : 'password'"
                          :append-icon="showPassword ? 'visibility_off' : 'visibility'"
                          :rules="passwordRules"
                          :error-messages="fieldErrors('password')"
                          hint="Mínimo 8 caracteres"
                          @click:append="showPassword = !showPassword"
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field
                          v-model="form.password_confirm"
                          label="Confirmar contraseña"
                          :type="showPassword ? 'text' : 'password'"
                          :rules="passwordConfirmRules"
                          :error-messages="fieldErrors('password_confirm')"
                        ></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-form>
                  <div class="step-actions text-xs-right">
                    <v-btn color="primary" large @click="nextStep('adminForm', 2)">
                      Continuar
                      <v-icon right>arrow_forward</v-icon>
                    </v-btn>
                  </div>
                </v-stepper-content>

                <!-- Paso 2: empresa -->
                <v-stepper-content step="2">
                  <v-form ref="companyForm">
                    <div class="step-title">Información de la empresa</div>
                    <div class="step-hint">Datos de contacto, ubicación y clasificación.</div>
                    <v-text-field :value="companyName" label="Nombre de la empresa" prepend-icon="business" readonly class="px-2"></v-text-field>
                    <v-layout row wrap>
                      <v-flex xs12 class="px-2">
                        <v-text-field v-model="form.company_address" label="Dirección" prepend-icon="place" :rules="requiredRules" :error-messages="fieldErrors('company_address')"></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field v-model="form.company_telephone" label="Teléfono de la empresa" prepend-icon="phone" :rules="requiredRules" :error-messages="fieldErrors('company_telephone')"></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field v-model="form.company_email" label="Correo de la empresa" prepend-icon="email" type="email" :rules="emailRules" :error-messages="fieldErrors('company_email')"></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm6 class="px-2">
                        <v-text-field v-model="form.company_web_page" label="Página web (opcional)" prepend-icon="language"></v-text-field>
                      </v-flex>
                    </v-layout>

                    <div class="step-subtitle">Ubicación</div>
                    <v-layout row wrap>
                      <v-flex xs12 class="px-2">
                        <v-text-field v-model="form.company_url_map" label="URL de mapa (opcional)" prepend-icon="map"></v-text-field>
                      </v-flex>
                    </v-layout>
                  </v-form>
                  <div class="step-actions">
                    <v-btn flat large @click="currentStep = 1">
                      <v-icon left>arrow_back</v-icon>
                      Atrás
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" large @click="nextStep('companyForm', 3)">
                      Continuar
                      <v-icon right>arrow_forward</v-icon>
                    </v-btn>
                  </div>
                </v-stepper-content>

                <!-- Paso 3: documentos -->
                <v-stepper-content step="3">
                  <div class="step-title">Documentos</div>
                  <div class="step-hint">Imágenes JPG o PNG de hasta 5 MB.</div>
                  <v-layout row wrap>
                    <v-flex v-for="doc in documents" :key="doc.key" xs12 md4 class="pa-2">
                      <div class="file-label">{{ doc.label }}</div>
                      <label class="file-drop" :class="{ 'has-file': files[doc.key], 'has-error': fileErrors[doc.key] || fieldErrors(doc.key).length }">
                        <input type="file" accept="image/jpeg,image/png" @change="selectFile($event, doc.key)">
                        <img v-if="previews[doc.key]" :src="previews[doc.key]" class="file-preview" alt="">
                        <template v-else>
                          <v-icon size="36" color="grey">cloud_upload</v-icon>
                          <span class="file-hint">Seleccionar imagen</span>
                        </template>
                      </label>
                      <div v-if="files[doc.key]" class="file-name">{{ files[doc.key].name }}</div>
                      <div class="error-text">{{ fileErrors[doc.key] || fieldErrors(doc.key)[0] }}</div>
                    </v-flex>
                  </v-layout>

                  <v-checkbox
                    v-model="consent"
                    class="mt-3 px-2"
                    label="Confirmo que la información proporcionada es correcta."
                    :error-messages="consentError ? ['Debes confirmar la información.'] : []"
                    @change="consentError = false"
                  ></v-checkbox>

                  <v-alert v-if="generalError" type="error" :value="true" class="mt-3">
                    {{ generalError }}
                  </v-alert>
                  <div class="step-actions">
                    <v-btn flat large @click="currentStep = 2">
                      <v-icon left>arrow_back</v-icon>
                      Atrás
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" large :loading="submitting" @click="submit">
                      Completar registro
                    </v-btn>
                  </div>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

const COMPANY_DEFAULTS = {
  company_scian: 0,
  company_classification: 'Sin clasificar',
  company_latitude: 0,
  company_longitude: 0,
  company_validity: '2099-12-31'
}

const API_BASE_URL = process.env.VUE_APP_API_URL || 'https://empresa-service.intrare.app'

export default {
  name: 'CompanyOnboarding',
  data() {
    return {
      token: '',
      validating: true,
      submitting: false,
      tokenValid: false,
      completed: false,
      currentStep: 1,
      showPassword: false,
      consentError: false,
      previews: { company_logo: '', ine_frente: '', ine_atras: '' },
      documents: [
        { key: 'company_logo', label: 'Logo de la empresa *' },
        { key: 'ine_frente', label: 'INE frente (opcional)' },
        { key: 'ine_atras', label: 'INE reverso (opcional)' }
      ],
      companyName: '',
      buildingName: '',
      adminEmail: '',
      expiresAt: '',
      generalError: '',
      errors: {},
      fileErrors: {},
      consent: false,
      files: { company_logo: null, ine_frente: null, ine_atras: null },
      form: {
        first_name: '',
        last_name: '',
        celular: '',
        position: '',
        password: '',
        password_confirm: '',
        company_address: '',
        company_telephone: '',
        company_email: '',
        company_web_page: '',
        company_url_map: '',
        // Campos requeridos por la API que ya no se piden en el formulario
        ...COMPANY_DEFAULTS
      },
      requiredRules: [value => !!value || 'Este campo es requerido.'],
      phoneRules: [
        value => !!value || 'El teléfono es requerido.',
        value => (value && value.replace(/\D/g, '').length >= 10) || 'Ingresa al menos 10 dígitos.'
      ],
      emailRules: [
        value => !!value || 'El correo es requerido.',
        value => /.+@.+\..+/.test(value) || 'Ingresa un correo válido.'
      ],
      passwordRules: [
        value => !!value || 'La contraseña es requerida.',
        value => (value && value.length >= 8) || 'Debe contener al menos 8 caracteres.'
      ]
    }
  },
  computed: {
    passwordConfirmRules() {
      return [
        value => !!value || 'Confirma la contraseña.',
        value => value === this.form.password || 'Las contraseñas no coinciden.'
      ]
    }
  },
  beforeDestroy() {
    Object.keys(this.previews).forEach(key => this.previews[key] && URL.revokeObjectURL(this.previews[key]))
  },
  mounted() {
    this.token = this.$route.params.token || ''
    this.validateToken()
  },
  methods: {
    validateToken() {
      if (!this.token) {
        this.validating = false
        this.generalError = 'La invitación no contiene un token válido.'
        return
      }
      axios.post(`${API_BASE_URL}/Parking/company-onboarding/validate-token/`, { token: this.token })
        .then(response => {
          this.tokenValid = response.data.valid === true
          this.companyName = response.data.company_name || ''
          this.buildingName = response.data.building_name || ''
          this.adminEmail = response.data.admin_email || ''
          this.expiresAt = this.formatDate(response.data.expires_at)
        })
        .catch(error => {
          const data = error.response && error.response.data
          this.generalError = data && data.token ? data.token[0] : 'El enlace es inválido o ha expirado.'
        })
        .finally(() => { this.validating = false })
    },
    nextStep(formRef, step) {
      if (this.$refs[formRef].validate()) this.currentStep = step
    },
    setPreview(key, file) {
      if (this.previews[key]) URL.revokeObjectURL(this.previews[key])
      this.$set(this.previews, key, file ? URL.createObjectURL(file) : '')
    },
    selectFile(event, key) {
      const file = event.target.files && event.target.files[0]
      this.$delete(this.fileErrors, key)
      this.setPreview(key, null)
      if (!file) {
        this.$set(this.files, key, null)
        return
      }
      if (['image/jpeg', 'image/png'].indexOf(file.type) === -1) {
        this.$set(this.fileErrors, key, 'Usa una imagen JPG o PNG.')
        this.$set(this.files, key, null)
        event.target.value = ''
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$set(this.fileErrors, key, 'La imagen no puede superar 5 MB.')
        this.$set(this.files, key, null)
        event.target.value = ''
        return
      }
      this.$set(this.files, key, file)
      this.setPreview(key, file)
    },
    fieldErrors(field) {
      const value = this.errors[field]
      if (!value) return []
      return Array.isArray(value) ? value : [value]
    },
    submit() {
      this.errors = {}
      this.generalError = ''
      if (!this.$refs.adminForm.validate()) { this.currentStep = 1; return }
      if (!this.$refs.companyForm.validate()) { this.currentStep = 2; return }
      if (!this.files.company_logo) {
        this.$set(this.fileErrors, 'company_logo', 'El logo de la empresa es requerido.')
        return
      }
      if (!this.consent) {
        this.consentError = true
        return
      }

      const formData = new FormData()
      formData.append('token', this.token)
      Object.keys(this.form).forEach(key => formData.append(key, this.form[key]))
      Object.keys(this.files).forEach(key => {
        if (this.files[key]) formData.append(key, this.files[key])
      })

      this.submitting = true
      axios.post(`${API_BASE_URL}/Parking/company-onboarding/complete/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      })
        .then(() => {
          this.completed = true
        })
        .catch(error => {
          const data = error.response && error.response.data
          if (data && typeof data === 'object') {
            this.errors = data
            this.currentStep = this.stepForErrors(data)
            this.generalError = data.error || 'Revisa los campos marcados e intenta nuevamente.'
          } else {
            this.generalError = 'No fue posible completar el registro. Intenta nuevamente.'
          }
        })
        .finally(() => { this.submitting = false })
    },
    stepForErrors(errors) {
      const adminFields = ['first_name', 'last_name', 'celular', 'position', 'password', 'password_confirm']
      const fileFields = ['company_logo', 'ine_frente', 'ine_atras']
      const keys = Object.keys(errors)
      if (keys.some(k => adminFields.indexOf(k) !== -1)) return 1
      if (keys.some(k => k.indexOf('company_') === 0 && fileFields.indexOf(k) === -1)) return 2
      return 3
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleString('es-MX')
    }
  }
}
</script>

<style scoped>
.company-onboarding-page {
  min-height: 100vh;
  padding: 30px 0;
  background: #eef3f5;
}
.onboarding-card { overflow: hidden; border-radius: 8px; }
.onboarding-header {
  display: flex;
  align-items: center;
  padding: 24px 28px;
}
.header-icon { font-size: 40px; margin-right: 16px; opacity: 0.9; }
.header-subtitle { opacity: 0.8; }
.onboarding-stepper { background: transparent; box-shadow: none; }
.step-title { color: #002b48; font-size: 20px; font-weight: 500; padding: 0 8px; }
.step-hint { color: #78909c; font-size: 14px; margin: 2px 0 16px; padding: 0 8px; }
.step-subtitle {
  color: #45666a;
  font-weight: 500;
  margin: 8px 8px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #dfe7e9;
}
.step-actions { display: flex; align-items: center; margin-top: 24px; }
.step-actions.text-xs-right { justify-content: flex-end; }
.file-label { font-weight: 500; margin-bottom: 8px; color: #45666a; }
.file-drop {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 150px;
  border: 2px dashed #b0bec5;
  border-radius: 8px;
  background: #f7fafb;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s;
}
.file-drop:hover { border-color: #002b48; background: #eef3f5; }
.file-drop.has-file { border-style: solid; border-color: #4caf50; }
.file-drop.has-error { border-color: #ff5252; }
.file-drop input[type="file"] { position: absolute; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.file-hint { color: #78909c; font-size: 13px; margin-top: 6px; }
.file-preview { max-width: 100%; max-height: 100%; object-fit: contain; }
.file-name { font-size: 12px; color: #78909c; margin-top: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.error-text { color: #ff5252; font-size: 12px; min-height: 18px; margin-top: 5px; }
</style>
